
// PATHS
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const FIND_GENERAL_TIMEFRAME = '/network/timeframe/findGeneralTimes';
const FIND_SPECIFIC_TIMEFRAME = '/network/timeframe/findSpecificTimes';
const FIND_OPEN_CLOSE_TIMEFRAME = 'network/timeframe/findTimeFrame';
const CREATE_SPECIFIC_TIMEFRAME = 'network/timeframe/createSpecificTimes';
const CREATE_GENERAL_TIMEFRAME = 'network/timeframe/createGeneralTimes';
const CHECK_ACTIVATION = 'network/ms/activation';
const GET_GENERAL_DATA_ACTIVATION = 'network/ms/findActivationDetail';
const SAVE_BOOKABILITY_VISIBILITY = 'network/bookAbilityVisibility/create';

/// If it has an isoString, it'll return it with the selected timezone,
/// if timezone is undefined it'll be set as Rome by default, 'cause why not
export const convertUtcToTzIsoDayJs = (isoString: string | null | undefined, tz: string = "Europe/Rome"): dayjs.Dayjs | null => {
    if (!isoString) return null;

    // Convert UTC to target timezone
    const inDmsTz = dayjs.utc(isoString).tz(tz);

    // Recreate a "local" datetime keeping Y/M/D/H/M/S, without timezone
    return dayjs()
        .year(inDmsTz.year())
        .month(inDmsTz.month())
        .date(inDmsTz.date())
        .hour(inDmsTz.hour())
        .minute(inDmsTz.minute())
        .second(inDmsTz.second())
        .millisecond(inDmsTz.millisecond())
};

// Converte stringa ISO UTC in una stringa che il TimePicker mostrerà come orario del DMS
// creiamo una data "locale" con l'ora del DMS, così il TimePicker la mostra correttamente
export const convertUtcToTzIsoStringWithFormat = (isoString: string | null | undefined, tz: string, format: string): string | null => {
    if (!isoString) return null;

    return convertUtcToTzIsoDayJs(isoString, tz)?.format(format) ?? null;
};

// Converte stringa ISO UTC in una stringa che il TimePicker mostrerà come orario del DMS
// creiamo una data "locale" con l'ora del DMS, così il TimePicker la mostra correttamente
export const convertUtcToTzIsoString = (isoString: string | null | undefined, tz: string): string | null => {
    if (!isoString) return null;

    return convertUtcToTzIsoDayJs(isoString, tz)?.format() ?? null;
};

// Converte l'orario "visivo", che l'utente vede come orario del DMS, in UTC per il backend
// L'utente imposta 08:00 pensando sia l'orario di Lisbona, dobbiamo salvare 08:00 UTC (se Lisbona = UTC)
const convertVisualTimeToUtc = (value: any, dmsTimezone: string): string | null => {
    if (!value) return null;
    const d = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!d.isValid()) return null;
    // Estrai l'ora "visiva" (quella che l'utente vede)
    const hour = d.hour();
    const minute = d.minute();
    const second = d.second();
    // Crea una data nel timezone del DMS con quell'ora, poi convertila in UTC
    const inDmsTz = dayjs.tz(`2026-01-19T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`, dmsTimezone);
    return inDmsTz.utc().format();
};

export const convertLocalToTzIso = (localDateString: string, tz: string): string => {
    const localDate = dayjs(localDateString);

    const tzDate = dayjs.tz(
        `${localDate.year()}-${String(localDate.month() + 1).padStart(2, '0')}-${String(localDate.date()).padStart(2, '0')} ${String(localDate.hour()).padStart(2, '0')}:${String(localDate.minute()).padStart(2, '0')}:${String(localDate.second()).padStart(2, '0')}`,
        tz
    );

    return tzDate.toISOString();
};

const findGeneralTimeframe: any = async (req: any) => {
    const response = await axios.post(FIND_GENERAL_TIMEFRAME, req);
    const data = response.data;
    const tz = data.zoneId || "Europe/Rome";

    // Converti gli orari UTC nel timezone del DMS (restituendo stringhe ISO)
    if (data.generalTimeFrame) {
        data.generalTimeFrame = data.generalTimeFrame.map((tf: any) => ({
            ...tf,
            opening: convertUtcToTzIsoString(tf.opening, tz),
            breakFrom: convertUtcToTzIsoString(tf.breakFrom, tz),
            breakTo: convertUtcToTzIsoString(tf.breakTo, tz),
            closing: convertUtcToTzIsoString(tf.closing, tz),
        }));
    }
    return data;
}

const findSpecificTimeframe: any = async (req: any) => {
    const response = await axios.post(FIND_SPECIFIC_TIMEFRAME, req);
    const data = response.data;
    const tz = data.timezoneId || "Europe/Rome";

    // Converti gli orari UTC nel timezone del DMS (restituendo stringhe ISO)
    if (data.specificTimeFrame) {
        data.specificTimeFrame = data.specificTimeFrame.map((tf: any) => ({
            ...tf,
            opening: convertUtcToTzIsoString(tf.opening, tz),
            breakFrom: convertUtcToTzIsoString(tf.breakFrom, tz),
            breakTo: convertUtcToTzIsoString(tf.breakTo, tz),
            closing: convertUtcToTzIsoString(tf.closing, tz),
        }));
    }
    return data;
}

const findOpenCloseTimeframe: any = async (req: any) => {
    const response = await axios.post(FIND_OPEN_CLOSE_TIMEFRAME, req);
    const data = response.data;
    const tz = data.timezoneId || "Europe/Rome";

    // Converti gli orari UTC nel timezone del DMS
    if (data.timeFrame) {
        data.timeFrame = data.timeFrame.map((tf: any) => ({
            ...tf,
            opening: convertUtcToTzIsoString(tf.opening, tz),
            breakFrom: convertUtcToTzIsoString(tf.breakFrom, tz),
            breakTo: convertUtcToTzIsoString(tf.breakTo, tz),
            closing: convertUtcToTzIsoString(tf.closing, tz),
        }));
    }
    return data;
}

const createGeneralTimeframe: any = async (req: any) => {
    const dmsTimezone = req.dmsTimezone || "Europe/Rome";
    // Converti gli orari "visivi" in UTC per il backend
    const reqToSend = {
        id: req.id,
        generalTimeFrame: req.generalTimeFrame?.map((tf: any) => ({
            ...tf,
            opening: convertVisualTimeToUtc(tf.opening, dmsTimezone),
            breakFrom: convertVisualTimeToUtc(tf.breakFrom, dmsTimezone),
            breakTo: convertVisualTimeToUtc(tf.breakTo, dmsTimezone),
            closing: convertVisualTimeToUtc(tf.closing, dmsTimezone),
        }))
    };
    const response = await axios.post(CREATE_GENERAL_TIMEFRAME, reqToSend);
    const data = response.data;
    // Usa il timezone dalla response se disponibile, altrimenti usa quello passato nella request
    const tz = data.zoneId || dmsTimezone;

    // Converti la risposta nel timezone del DMS
    if (data.generalTimeFrame) {
        data.generalTimeFrame = data.generalTimeFrame.map((tf: any) => ({
            ...tf,
            opening: convertUtcToTzIsoString(tf.opening, tz),
            breakFrom: convertUtcToTzIsoString(tf.breakFrom, tz),
            breakTo: convertUtcToTzIsoString(tf.breakTo, tz),
            closing: convertUtcToTzIsoString(tf.closing, tz),
        }));
    }
    return data;
}

const createSpecificTimeframe: any = async (req: any) => {
    const dmsTimezone = req.dmsTimezone || "Europe/Rome";
    // Converti gli orari "visivi" in UTC per il backend
    const reqToSend = {
        id: req.id,
        specificTimeFrame: req.specificTimeFrame?.map((tf: any) => ({
            ...tf,
            opening: convertVisualTimeToUtc(tf.opening, dmsTimezone),
            breakFrom: convertVisualTimeToUtc(tf.breakFrom, dmsTimezone),
            breakTo: convertVisualTimeToUtc(tf.breakTo, dmsTimezone),
            closing: convertVisualTimeToUtc(tf.closing, dmsTimezone),
        }))
    };
    const response = await axios.post(CREATE_SPECIFIC_TIMEFRAME, reqToSend);
    const data = response.data;
    // Usa il timezone dalla response se disponibile, altrimenti usa quello passato nella request
    const tz = data.timezoneId || dmsTimezone;

    // Converti la risposta nel timezone del DMS
    if (data.specificTimeFrame) {
        data.specificTimeFrame = data.specificTimeFrame.map((tf: any) => ({
            ...tf,
            opening: convertUtcToTzIsoString(tf.opening, tz),
            breakFrom: convertUtcToTzIsoString(tf.breakFrom, tz),
            breakTo: convertUtcToTzIsoString(tf.breakTo, tz),
            closing: convertUtcToTzIsoString(tf.closing, tz),
        }));
    }
    return data;
}

const checkInfoActivation: any = async (req: any) => {
    const response = await axios.post(CHECK_ACTIVATION, req);
    return response.data;
}

const getGeneralDataActivation: any = async (req: any) => {
    const response = await axios.post(GET_GENERAL_DATA_ACTIVATION, req);
    return response.data;
}

const saveBookabilityVisibility: any = async (req: any) => {
    const response = await axios.post(SAVE_BOOKABILITY_VISIBILITY, req);
    return response.data;
}
export {
    findGeneralTimeframe,
    findSpecificTimeframe,
    findOpenCloseTimeframe,
    createSpecificTimeframe,
    createGeneralTimeframe,
    checkInfoActivation,
    getGeneralDataActivation,
    saveBookabilityVisibility
}